export const SSO_PROTOCOLS = Object.freeze({
  OIDC: "OIDC",
  CAS: "CAS",
});

export const SSO_PARTNERS = Object.freeze({
  NU: "NU",
  EMU: "EMU",
});

export const SSO_CALLBACK_PATH = "/auth/sso/callback";
export const SSO_AUTHORIZATION_HOST = "login.microsoftonline.com";
export const SSO_LOCAL_LOGIN_PATH = "/login?type=login";
export const SSO_DEFAULT_PURCHASE_PATH = "/buynow";
export const SSO_PURCHASE_RETURN_PATHS = Object.freeze([
  SSO_DEFAULT_PURCHASE_PATH,
  "/ebuynow",
]);
export const SSO_PURCHASE_SUCCESS_PATH = "/buynow/success";

export const SSO_AUTH_QUERY_KEYS = Object.freeze([
  "access_token",
  "authorization",
  "code",
  "crm_token",
  "crmtoken",
  "error",
  "error_description",
  "id_token",
  "mfa",
  "nonce",
  "otp",
  "password",
  "refresh_token",
  "session_state",
  "state",
  "ticket",
  "token",
  "v2callback",
  "v2locationid",
]);

export const SSO_HOSTS = Object.freeze({
  production: "go.lazparking.com",
  nonProduction: Object.freeze([
    "localhost:3000",
    "stg.grswebui.thinxsoftware.com",
  ]),
});

export const SSO_HANDOFF_URLS = Object.freeze({
  production: Object.freeze(["https://laz.io/api/sso/handoff"]),
  nonProduction: Object.freeze([
    "https://dev.laz.io/api/sso/handoff",
    "https://qa.laz.io/api/sso/handoff",
    "https://stg.laz.io/api/sso/handoff",
  ]),
});

export const SSO_LOGOUT_URLS = Object.freeze({
  oidc:
    "https://login.microsoftonline.com/a8eec281-aaa3-4dae-ac9b-9a398b9215e7/oauth2/v2.0/logout",
  cas: Object.freeze({
    [SSO_PARTNERS.NU]: Object.freeze({
      production: "https://neuidmsso.neu.edu/idp/profile/cas/logout",
      nonProduction:
        "https://neuidmssotest.neu.edu/idp/profile/cas/logout",
    }),
    [SSO_PARTNERS.EMU]: Object.freeze({
      production: "https://emusso.emich.edu/cas/logout",
      nonProduction: "https://netidtest.emich.edu/cas/logout",
    }),
  }),
});

export const ENTRA_LOGOUT_URL = SSO_LOGOUT_URLS.oidc;

const STATIC_EMU_CAS_PROVIDER = Object.freeze({
  id: 0,
  name: "EMU CAS",
  authProtocol: SSO_PROTOCOLS.CAS,
});

export const SSO_LOCATIONS = Object.freeze({
  "81354": Object.freeze({
    partner: SSO_PARTNERS.NU,
    staticProvider: null,
    showOriginalLotReservationText: true,
    hideTimePickerLocationInfoIds: Object.freeze(["99981354"]),
  }),
  "155882": Object.freeze({
    partner: SSO_PARTNERS.NU,
    staticProvider: null,
    showOriginalLotReservationText: false,
    hideTimePickerLocationInfoIds: Object.freeze([]),
  }),
  "99960994": Object.freeze({
    partner: SSO_PARTNERS.EMU,
    staticProvider: STATIC_EMU_CAS_PROVIDER,
    showOriginalLotReservationText: true,
    hideTimePickerLocationInfoIds: Object.freeze(["60994"]),
  }),
  "97258": Object.freeze({
    partner: SSO_PARTNERS.EMU,
    staticProvider: STATIC_EMU_CAS_PROVIDER,
    showOriginalLotReservationText: true,
    hideTimePickerLocationInfoIds: Object.freeze([]),
  }),
});

export const SSO_LOCATION_IDS = Object.freeze(Object.keys(SSO_LOCATIONS));

function normalize(value) {
  return String(value ?? "").trim();
}

export function getSsoLocationConfig(locationId) {
  return SSO_LOCATIONS[normalize(locationId)] || null;
}

export function getSsoPartner(locationId) {
  return getSsoLocationConfig(locationId)?.partner || null;
}

export function isSsoLocation(locationId) {
  return !!getSsoLocationConfig(locationId);
}

// A completed purchase intentionally leaves no access token. Restarting sign-in
// here would unmount BuyNow and destroy state.reservationConfirmation, which is
// the only copy of the receipt -- the screen is not reload-safe either, because
// getLocationDetails bounces to the rate list once the cart is empty.
export function isPurchaseSuccessPath(
  pathname,
  contextRoot = process.env.REACT_APP_CONTEXT_ROOT || ""
) {
  return (
    String(pathname ?? "").replace(contextRoot, "") ===
    SSO_PURCHASE_SUCCESS_PATH
  );
}

// The router's buynow route has no `exact`, so it also matches
// /buynow/success. This decides whether that route should hand off to
// ExternalBuyNow, which restarts SSO sign-in from a mount effect.
export function shouldRestartSsoSignIn({
  locationId,
  urlToken,
  hasValidSession,
  isStoredTokenExpired = false,
  pathname,
  contextRoot,
}) {
  return (
    isSsoLocation(locationId) &&
    !urlToken &&
    (!hasValidSession || !!isStoredTokenExpired) &&
    !isPurchaseSuccessPath(pathname, contextRoot)
  );
}

export function getStaticSsoProvider(locationId) {
  return getSsoLocationConfig(locationId)?.staticProvider || null;
}

export function shouldShowOriginalLotReservationText(locationId) {
  return !!getSsoLocationConfig(locationId)?.showOriginalLotReservationText;
}

export function shouldHideSsoTimePicker(locationInfoId) {
  const normalizedId = normalize(locationInfoId);
  return Object.values(SSO_LOCATIONS).some((location) =>
    location.hideTimePickerLocationInfoIds.includes(normalizedId)
  );
}

export function isProductionSsoHost(hostname) {
  return normalize(hostname).toLowerCase() === SSO_HOSTS.production;
}

export function getSsoHostEnvironment(host) {
  const normalizedHost = normalize(host).toLowerCase();
  if (normalizedHost === SSO_HOSTS.production) return "production";
  if (SSO_HOSTS.nonProduction.includes(normalizedHost)) {
    return "nonProduction";
  }
  return null;
}

export function getCasLogoutUrl(partner, host) {
  const environment = getSsoHostEnvironment(host);
  const normalizedPartner = normalize(partner).toUpperCase();
  if (!environment) return "";
  return SSO_LOGOUT_URLS.cas[normalizedPartner]?.[environment] || "";
}

// Off by design. CAS behaved the same way before the Entra migration -- it sent
// users to the IdP logout endpoint with no return parameter -- so stopping on the
// provider's page is the long-standing behavior, not a regression. Turning this on
// sends users to the site root, and the outstanding request is to return them to
// the location page instead, so leave it false until that destination is built.
export const REDIRECT_USER_TO_HOME_ON_LOGOUT = false;

// Without post_logout_redirect_uri Entra strands the user on a Microsoft
// "you signed out" page. Entra rejects the parameter unless it exactly matches a
// redirect URI on the app registration, so only send it for hosts we own.
export function getEntraLogoutUrl(
  origin = window.location.origin,
  { redirectToHome = REDIRECT_USER_TO_HOME_ON_LOGOUT } = {}
) {
  const base = SSO_LOGOUT_URLS.oidc;
  if (!redirectToHome) return base;

  const normalizedOrigin = normalize(origin);
  if (!normalizedOrigin) return base;

  let parsedOrigin;
  try {
    parsedOrigin = new URL(normalizedOrigin);
  } catch (error) {
    return base;
  }

  if (!getSsoHostEnvironment(parsedOrigin.host)) return base;

  const logoutUrl = new URL(base);
  logoutUrl.searchParams.set("post_logout_redirect_uri", parsedOrigin.origin);
  return logoutUrl.toString();
}

export function getAllowedSsoHandoffUrls(hostname) {
  return isProductionSsoHost(hostname)
    ? SSO_HANDOFF_URLS.production
    : SSO_HANDOFF_URLS.nonProduction;
}
